import React from 'react';

import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { FlashAvailability } from '~/hooks/apis/calendar/useFlashAvailability';
import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';

import { formatDateWeekDayShortMonth } from '~/services/TimeService';

import { Bundle, HotelBundleComponent } from '~/types/bundles/bundle';

import TourSpaces from './TourSpaces';
import { HotelStatus, checkInDate, hotelStatus } from './hotelStay';
import useBundleDepartureChecks from './useBundleDepartureChecks';

interface Props {
  bundle: Bundle;
}

function HotelHeader({ hotel }: { hotel: HotelBundleComponent }) {
  const { data: offer } = useGetOffer(hotel.offer_id);
  return <>{offer?.name ?? 'Hotel'} · check-in</>;
}

const HOTEL_STATUS_CHIPS: Record<HotelStatus, { label: string; color: 'success' | 'error' | 'warning' }> = {
  available: { label: 'Available', color: 'success' },
  soldOut: { label: 'Sold out', color: 'error' },
  noRates: { label: 'No rates', color: 'warning' },
};

interface HotelCellProps {
  availability: FlashAvailability | undefined;
  checkIn: string;
  error: Error | null;
}

function HotelCell({ availability, checkIn, error }: HotelCellProps) {
  const status = hotelStatus(availability, checkIn);
  const rooms = (occupancy: number) => availability?.[occupancy]?.[checkIn]?.rooms ?? '–';
  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Typography variant="body2">{formatDateWeekDayShortMonth(checkIn)}</Typography>
      {!!error && (
        <Tooltip title={error.message}>
          <Chip size="small" variant="outlined" label="Can't check" color="warning" />
        </Tooltip>
      )}
      {!error && status === undefined && <CircularProgress size={14} />}
      {!error && status !== undefined && (
        <Tooltip
          title={
            status === 'noRates'
              ? "The flash calendar has no rates for this check-in, so it can't be sold"
              : `Rooms left: single ${rooms(1)}, double ${rooms(2)}`
          }
        >
          <Chip
            size="small"
            variant="outlined"
            label={HOTEL_STATUS_CHIPS[status].label}
            color={HOTEL_STATUS_CHIPS[status].color}
          />
        </Tooltip>
      )}
    </Box>
  );
}

export default function BundleDepartureChecks({ bundle }: Props) {
  const { tour, hotels, departures, tourDepartureOn, hotelResults, isChecking, tourError, hotelError, blocked } =
    useBundleDepartureChecks(bundle);

  if (!tour || departures.length === 0) return null;

  const results = hotelResults;
  const failed = !!tourError || !!hotelError;

  return (
    <Box>
      <Box mb={2}>
        {!!tourError && <Alert severity="warning">Couldn't check the tour's spaces: {tourError.message}</Alert>}
        {!!hotelError && <Alert severity="warning">Couldn't check hotel availability: {hotelError.message}</Alert>}
        {!failed && !isChecking && blocked.length === 0 && (
          <Alert severity="success">Every component is available on all {departures.length} departures.</Alert>
        )}
        {!failed && !isChecking && blocked.length > 0 && (
          <Alert severity="warning">
            {blocked.length} of {departures.length} departures can't be sold as a single and a double: the tour has no
            space, or the hotel is sold out or has no rates. Remove those departures or choose another hotel.
          </Alert>
        )}
      </Box>

      <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Departs</TableCell>
              <TableCell>Tour · spaces left</TableCell>
              {hotels.map((hotel) => (
                <TableCell key={hotel.id}>
                  <HotelHeader hotel={hotel} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {departures.map((date) => (
              <TableRow key={date} selected={blocked.includes(date)}>
                <TableCell>{formatDateWeekDayShortMonth(date)}</TableCell>
                <TableCell>
                  {tourError ? (
                    <Tooltip title={tourError.message}>
                      <Chip size="small" variant="outlined" label="Can't check" color="warning" />
                    </Tooltip>
                  ) : (
                    <TourSpaces departure={tourDepartureOn(date)} />
                  )}
                </TableCell>
                {hotels.map((hotel, index) => (
                  <TableCell key={hotel.id}>
                    <HotelCell
                      availability={results[index].data}
                      checkIn={checkInDate(date, hotel.day_offset ?? 0)}
                      error={results[index].error}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
